import { core } from '@musinsa/pbo-core'
import { NIKE_IDENTIFIER_FIELDS, requiredFieldsFor, usesNikeIdentifiers } from '../queries/requiredFields'
import type { InboundRow } from './types'

// .handoff/gr-gr01__20260826-1424 spec.md · schema.after.json 기준.
// 컬럼 제목/폭/순서/렌더는 스펙 그대로, 옵션값은 moms-be·MOMS_COMBO_INFO 실측으로 채웠다.
// (결정 02 §4 — 시각 계약은 데모, 데이터 계약은 BE 실측이 정본)

/** 라벨지 규격 — spec.md inboundBarcodePrintModal-bpSpecSelect */
export const BARCODE_SPECS = ['A4(3X4)', 'A4(3X8)', 'A4(바코드)', '라벨(1X2)'] as const
export type BarcodeSpec = (typeof BARCODE_SPECS)[number]
export const DEFAULT_BARCODE_SPEC: BarcodeSpec = 'A4(3X8)'

/** 바코드 출력 최대 매수 — spec.md inboundBarcodePrintModal-bpQuantityInput */
export const BARCODE_COPIES_MAX = 99

/**
 * 수량·입고단가 셀의 입력 자릿수.
 *
 * pbo-core amountInput 은 maxLength 기본값이 5 라 안 주면 여섯 자리부터 잘린다. 표시값도
 * 잘린 값이라 조회 직후부터 틀리고, 그 셀을 건드리면 잘린 값이 그대로 저장된다.
 * 10 은 BE 가 받는 범위(quantity Int, purchasePrice BigDecimal) 안에서 실무 자릿수를 덮는 값이다.
 */
export const AMOUNT_MAX_LENGTH = 10

/** 상세사유 최소 길이 — 1.0 RemarkPop 검증과 동일 */
export const REASON_DETAIL_MIN_LENGTH = 5
export const REASON_DETAIL_MAX_LENGTH = 200

/** [MOMS 규칙] as-is 페이지 크기 10,000 고정 — 1.0 은 페이징 없이 전체 로드 */
export const GR01_PAGE_SIZE = 10000

/** 입고번호 다건 입력 상한 — BE @Size(max = 200) */
export const ORD_OPT_NO_MAX = 200

/** 삭제 요청 ids 상한 — BE DeleteNormalInboundsRequest @Size(max = 1000) */
export const DELETE_IDS_MAX = 1000

/** 엑셀 업로드 오류 메시지에 표시하는 셀·행 최대 개수 */
export const ERROR_CELL_DISPLAY_MAX = 10
export const ERROR_ROW_DISPLAY_MAX = 5

/**
 * 테이블 기본 컬럼은 컨덕터 트리 `samples/test/testInbound.ts` 계약을 따른다(`core.CoreColumnType`).
 * UID·옵션코드는 1.0 GR01 운영 메타와 같이 NIKE 계열 운영 화주에서만 추가하고, SKUID는 노출하지 않는다.
 * 편집 셀은 화주코드·입고예정일자·입고번호·거래유형·상품명·센터명·층정보·바코드·UID·옵션코드·박스번호·업체명.
 * 바코드 select 는 옵션을 넘겨야 한다. pbo-core 렌더러는 `variantProps.options` 만 읽고 데이터에서
 * 고유값을 만들어 주지 않는다 — 안 주면 목록이 늘 비어 셀을 고를 수 없다. 호출부가 현재 행의
 * 바코드 고유값을 모아 `buildColumns` 두 번째 인자로 넘긴다(GeneralInboundTable).
 *
 * 이 문단은 원래 정반대로 적혀 있었고("옵션을 주지 않는다 — Table 이 고유값으로 만든다") 그 문장이
 * 빈 목록 버그의 원인이었다. 고친 뒤에도 문장이 남아 있어 리뷰가 세 번 짚었다. 지우지 말 것.
 */
type IdentifierColumnKey = (typeof NIKE_IDENTIFIER_FIELDS)[number]['key']

const IDENTIFIER_COLUMNS: ReadonlySet<string> = new Set<IdentifierColumnKey>(
  NIKE_IDENTIFIER_FIELDS.map((field) => field.key),
)

const isIdentifierColumn = (dataIndex: core.CoreColumnType<InboundRow>['dataIndex']): boolean =>
  typeof dataIndex === 'string' && IDENTIFIER_COLUMNS.has(dataIndex)

const isRequired = (dataIndex: unknown, shipperCode: string, nikeIdentifiers: boolean) =>
  typeof dataIndex === 'string' && requiredFieldsFor(shipperCode, nikeIdentifiers).some((field) => field.key === dataIndex)

/** 복원 전 SKUID 컬럼 — NIKE_IDENTIFIERS_FLAG 가 꺼지면 이 컬럼을 보이고 UID·옵션코드는 화주와 무관하게 뺀다 */
const SKU_ID_COLUMN = 'SKU_ID'

/** 필수 표시는 화주별 필수 필드 정책에서 파생한다 — 표의 별표와 저장 전 검사가 갈라질 수 없다 */
export const buildColumns = (
  shipperOptions: { value: string; label: string }[],
  barcodeOptions: { value: string; label: string }[] = [],
  // 거래유형(LOT_ATTR5)·센터명(WH_NM) — GET /api/v1/combos 응답(화면에서 주입, LOGISPRD-12385).
  transactionTypeOptions: { value: string; label: string }[] = [],
  whNmOptions: { value: string; label: string }[] = [],
  shipperCode = '',
  // NIKE_IDENTIFIERS_FLAG 값. 기본값 true 는 정책 단위 테스트용이고 화면은 플래그 값을 넘긴다.
  nikeIdentifiers = true,
): core.CoreColumnType<InboundRow>[] => {
  const showIdentifiers = usesNikeIdentifiers(shipperCode, nikeIdentifiers)

  return rawColumns(shipperOptions, barcodeOptions, transactionTypeOptions, whNmOptions)
    .filter((column) => !isIdentifierColumn(column.dataIndex) || showIdentifiers)
    .filter((column) => column.dataIndex !== SKU_ID_COLUMN || !nikeIdentifiers)
    .map((column) => ({
      ...column,
      required: isRequired(column.dataIndex, shipperCode, nikeIdentifiers),
    }))
}

const rawColumns = (
  shipperOptions: { value: string; label: string }[],
  barcodeOptions: { value: string; label: string }[],
  transactionTypeOptions: { value: string; label: string }[],
  whNmOptions: { value: string; label: string }[],
): core.CoreColumnType<InboundRow>[] => [
  {
    key: 'c-sprNm',
    title: '화주코드',
    dataIndex: 'SPR_NM',
    width: 160,
    fixed: 'left',
    variant: 'select',
    variantProps: { options: shipperOptions, showSearch: true },
  },
  { key: 'c-plnDate', title: '입고예정일자', dataIndex: 'PLN_DATE', width: 150, variant: 'datepicker' },
  { key: 'c-ordOptNo', title: '입고번호', dataIndex: 'ORD_OPT_NO', width: 180, variant: 'input' },
  {
    key: 'c-lotAttr5',
    title: '거래유형',
    dataIndex: 'LOT_ATTR5',
    width: 120,
    variant: 'select',
    variantProps: { options: transactionTypeOptions },
  },
  { key: 'c-brdNm', title: '브랜드', dataIndex: 'BRD_NM', width: 160 },
  { key: 'c-gdsGroup1', title: '품목', dataIndex: 'GDS_GROUP1', width: 100 },
  { key: 'c-gdsNm', title: '상품명', dataIndex: 'GDS_NM', width: 280, variant: 'input' },
  { key: 'c-stlNo', title: '스타일넘버', dataIndex: 'STL_NO', width: 120 },
  // amountInput 은 maxLength 기본값이 5 다(pbo-core dist — slice(0,5) 한 값을 controlled value 로 쓴다).
  // 안 주면 129,000 이 조회 직후부터 12900 으로 보이고 그 값이 저장된다. 실제 자릿수를 명시한다.
  { key: 'c-ordQty', title: '수량', dataIndex: 'ORD_QTY', width: 100, align: 'right', variant: 'amountInput', variantProps: { maxLength: AMOUNT_MAX_LENGTH } },
  {
    key: 'c-whNm',
    title: '센터명',
    dataIndex: 'WH_NM',
    width: 120,
    variant: 'select',
    variantProps: { options: whNmOptions, showSearch: true },
  },
  { key: 'c-apPrice', title: '입고단가', dataIndex: 'AP_PRICE', width: 120, align: 'right', variant: 'amountInput', variantProps: { maxLength: AMOUNT_MAX_LENGTH } },
  { key: 'c-whArea', title: '층정보', dataIndex: 'WH_AREA', width: 100, variant: 'input' },
  // select 셀 렌더러는 variantProps.options 만 읽는다(pbo-core dist — dataSource 를 보지 않는다).
  // 안 주면 드롭다운이 늘 비어 편집이 불가능하다. 현재 행의 바코드 고유값을 넣는다.
  // TODO(api): 목록에 없는 새 바코드는 여기서 고를 수 없다. 바코드 마스터 조회가 열리면 그 옵션으로 바꾼다.
  {
    key: 'c-barcode',
    title: '바코드',
    dataIndex: 'BARCODE',
    width: 200,
    variant: 'select',
    variantProps: { options: barcodeOptions, showSearch: true },
  },
  { key: 'c-skuId', title: 'SKUID', dataIndex: SKU_ID_COLUMN, width: 150 },
  { key: 'c-uid', title: 'UID', dataIndex: 'UID', width: 150, variant: 'input' },
  { key: 'c-optionCode', title: '옵션코드', dataIndex: 'option_code', width: 160, variant: 'input' },
  { key: 'c-insDate', title: '생성일시', dataIndex: 'INS_DATE', width: 160 },
  { key: 'c-insBy', title: '생성자', dataIndex: 'INS_BY', width: 100 },
  { key: 'c-updDate', title: '수정일시', dataIndex: 'UPD_DATE', width: 160 },
  { key: 'c-updBy', title: '수정자', dataIndex: 'UPD_BY', width: 100 },
  { key: 'c-trsInvoiceNo', title: '박스번호', dataIndex: 'TRS_INVOICE_NO', width: 120, variant: 'input' },
  { key: 'c-comNm', title: '업체명', dataIndex: 'COM_NM', width: 160, variant: 'input' },
]

/** 엑셀 업로드 헤더 매핑 — BE 다운로드 양식(NormalInboundExcelDto)의 한글 헤더와 같은 이름을 쓴다 */
export const EXPORT_COLUMNS = [
  { key: 'SPR_NM', title: '화주코드' },
  { key: '_shipperName', title: '화주명' },
  { key: 'PLN_DATE', title: '입고예정일자' },
  { key: 'ORD_OPT_NO', title: '입고번호' },
  { key: 'ORD_TYPE', title: '입고유형' },
  { key: 'LOT_ATTR5', title: '거래유형' },
  { key: 'BRD_NM', title: '브랜드' },
  { key: 'GDS_GROUP1', title: '품목' },
  { key: 'GDS_NM', title: '상품명' },
  { key: 'STL_NO', title: '스타일넘버' },
  { key: 'ORD_QTY', title: '수량' },
  { key: 'AP_PRICE', title: '입고단가' },
  { key: 'WH_NM', title: '센터명' },
  { key: 'WH_AREA', title: '층정보' },
  { key: 'BARCODE', title: '바코드' },
  // 서버가 만드는 다운로드 xlsx와 무관한 업로드 헤더 매핑이다. 조회 화주와 다른 NIKE 행도
  // 업로드할 수 있으므로 UID·옵션코드는 화주 조건 없이 인식한다.
  { key: 'UID', title: 'UID' },
  { key: 'option_code', title: '옵션코드' },
  { key: 'TRS_INVOICE_NO', title: '박스번호' },
  { key: 'COM_NM', title: '업체명' },
  { key: 'INS_DATE', title: '생성일자' },
  { key: 'INS_BY', title: '생성자' },
  { key: 'UPD_DATE', title: '수정일자' },
  { key: 'UPD_BY', title: '수정자' },
] as const satisfies ReadonlyArray<{ readonly key: string; readonly title: string }>
